import { cn, Spinner } from '@heroui/react';
import { Key, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';

import { format } from 'date-fns';
import { StageTag, TaskStatusTag } from 'src/shared/components';
import { Product } from 'src/shared/types/product';

import {
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Typography
} from 'src/shared/components/ui';

import { columns } from '../constants/productColumns';
import getStageFromLifecycle from '../utils/getStageFromLifecycle';
import getStatus from '../utils/getStatus';
import {
  getProductLastUpdatedAt,
  sortByLastUpdated
} from '../utils/lastUpdated';
import ProductActionsCell from './ProductActionsCell';
import ProductNameCell from './ProductNameCell';

interface ProductsTableProps {
  isLoading?: boolean;
  products?: Product[];
  isSuccess?: boolean;
  header?: string;
  /** Used instead of `header` when there is exactly one product. */
  headerSingular?: string;
  /** Row shown in place of the products when the list is empty. */
  emptyPlaceholder?: string;
  /** Adds the Actions column (reorder, purchase sample). */
  showActions?: boolean;
}

const ProductsTable: React.FC<ProductsTableProps> = ({
  isLoading,
  products,
  isSuccess,
  header,
  headerSingular,
  emptyPlaceholder = 'No products found',
  showActions = false
}) => {
  const visibleColumns = showActions
    ? columns
    : columns.filter((column) => column.key !== 'actions');

  const renderCell = useCallback((item: Product, columnKey: Key) => {
    switch (columnKey) {
      case 'name':
        return (
          <ProductNameCell
            name={item.detail.name}
            image={item.image}
            id={item.id}
          />
        );

      case 'stage':
        return (
          <StageTag
            type={getStageFromLifecycle(item.detail.lifecycle) ?? 'draft'}
          />
        );

      case 'status':
        return <TaskStatusTag type={getStatus(item)} />;

      case 'updated': {
        const updatedAt = getProductLastUpdatedAt(item);

        return (
          <Typography variant="body-1" weight="medium">
            {updatedAt ? format(updatedAt, 'MMM dd, yyyy') : '-'}
          </Typography>
        );
      }

      default:
        return '';
    }
  }, []);

  const sortedProducts = useMemo(() => {
    if (!products) {
      return [];
    }

    return sortByLastUpdated(products, getProductLastUpdatedAt);
  }, [products]);

  const count = products?.length ?? 0;
  const title =
    count > 0
      ? `${count} ${count === 1 ? (headerSingular ?? header) : header}`
      : 'No products found';

  return (
    <div className="flex flex-col">
      <Typography variant="h4" as="h2" weight="bold">
        {isSuccess && <>{title}</>}
      </Typography>

      <Table
        aria-label="Products table"
        className={cn('heroui-table', count === 0 && 'pointer-events-none')}
      >
        <TableHeader columns={visibleColumns}>
          {(column) => (
            <TableColumn className="px-4" key={column.key}>
              {column.label}
            </TableColumn>
          )}
        </TableHeader>
        <TableBody
          items={sortedProducts}
          emptyContent={isLoading ? <Spinner /> : emptyPlaceholder}
        >
          {(item) => (
            <TableRow key={item.id}>
              {(columnKey) => (
                <TableCell>
                  {columnKey === 'actions' ? (
                    <ProductActionsCell
                      id={item.id}
                      lifecycle={item.detail.lifecycle}
                    />
                  ) : (
                    <Link
                      className="h-16 flex items-center"
                      to={`/products/${item.id}`}
                    >
                      <div className="truncate">
                        {renderCell(item, columnKey)}
                      </div>
                    </Link>
                  )}
                </TableCell>
              )}
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default ProductsTable;
